import type {
  PlantTrait,
  Plant,
  PlantAuthoringCompleteness,
  PlantLifecycle,
  PlantMorphology,
  PlantNativeRange,
  PlantPhenologyClimateBand,
  PlantPhenologyPhase,
  PlantRangeStatus,
  PlantSeedStarting,
  PlantSource,
  PlantTaxonomy,
  PlantTemperatureRequirements,
  PlantVariety,
} from '@/data/plants';
import { CANONICAL_PLANT_TRAITS } from '@/data/plants';
import { getEffectiveTempRequirements } from '@/lib/plantUtils';
import type { GeneratedPlant } from '@/lib/generatedProfiles';

export type CanonicalPlant = Plant & {
  // Legacy compatibility for existing callers that still branch on generated plants.
  isGenerated?: true;
  soil?: string;
  avoid?: string[];
  commonPests?: string[];
  indoorStart?: string | null;
  transplant?: string | null;
  directSow?: string | null;
  harvest?: string;
};

export interface NormalizePlantInputOptions {
  defaultSource?: PlantSource;
  sourceOverride?: Partial<PlantSource>;
  defaultStatus?: Plant['status'];
  fallbackId?: string;
  fallbackDescription?: string;
  includeCompatibilityAliases?: boolean;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

export const CANONICAL_PLANT_CATEGORIES: Plant['type'][] = ['vegetable', 'herb', 'fruit', 'flower'];
const CANONICAL_PLANT_TRAIT_SET = new Set<PlantTrait>(CANONICAL_PLANT_TRAITS);

const PLANT_TRAIT_ALIASES: Record<string, PlantTrait> = {
  culinary: 'culinary-herb',
  'culinary herb': 'culinary-herb',
  'culinary-herb': 'culinary-herb',
  medicinal: 'medicinal-tradition',
  'medicinal herb': 'medicinal-tradition',
  'medicinal tradition': 'medicinal-tradition',
  'medicinal-tradition': 'medicinal-tradition',
  tea: 'tea-herb',
  'tea herb': 'tea-herb',
  'tea-herb': 'tea-herb',
  'beneficial insect': 'beneficial-insect',
  'beneficial-insect': 'beneficial-insect',
  'cut flower': 'cut-flower',
  'cut-flower': 'cut-flower',
  'ground cover': 'groundcover',
  accumulator: 'dynamic-accumulator',
  'dynamic accumulator': 'dynamic-accumulator',
  'dynamic-accumulator': 'dynamic-accumulator',
  'cane fruit': 'cane-fruit',
  'cane-fruit': 'cane-fruit',
  'root crop': 'root-crop',
  'root-crop': 'root-crop',
  'clove planted': 'clove-planted',
  'clove-planted': 'clove-planted',
  'crown planted': 'crown-planted',
  'crown-planted': 'crown-planted',
  'division planted': 'division-planted',
  'division-planted': 'division-planted',
  naturalised: 'naturalized',
  aggressive: 'spreading-aggressive',
  spreading: 'spreading-aggressive',
  'spreading aggressive': 'spreading-aggressive',
  'spreading-aggressive': 'spreading-aggressive',
  container: 'container-friendly',
  'container friendly': 'container-friendly',
  'container-friendly': 'container-friendly',
};

function normalizePlantTraitValue(value: unknown): PlantTrait | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (CANONICAL_PLANT_TRAIT_SET.has(normalized as PlantTrait)) return normalized as PlantTrait;
  return PLANT_TRAIT_ALIASES[normalized];
}

export function normalizePlantTraits(...values: unknown[]): PlantTrait[] | undefined {
  const traits: PlantTrait[] = [];

  for (const value of values) {
    const candidates = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(/[,\n]/)
        : [];

    for (const candidate of candidates) {
      const trait = normalizePlantTraitValue(candidate);
      if (trait) traits.push(trait);
    }
  }

  const uniqueTraits = Array.from(new Set(traits));
  return uniqueTraits.length > 0 ? uniqueTraits : undefined;
}

export function normalizePlantCategoryInput(value: unknown): Plant['type'] {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'vegetable' || normalized === 'vegetables' || normalized === 'veggie' || normalized === 'veggies') return 'vegetable';
  if (normalized === 'herb' || normalized === 'herbs') return 'herb';
  if (normalized === 'fruit' || normalized === 'fruits') return 'fruit';
  if (normalized === 'flower' || normalized === 'flowers' || normalized === 'ornamental' || normalized === 'ornamentals') return 'flower';
  if (normalized === 'perennial' || normalized === 'perennials') return 'herb';
  if (normalized === 'tree' || normalized === 'shrub' || normalized === 'vine') {
    return 'fruit';
  }
  return 'vegetable';
}

function normalizeSunlight(value: unknown): Plant['sunlight'] {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'full-shade') return 'full-shade';
  if (
    normalized === 'full-sun-to-partial-shade' ||
    normalized === 'full sun to partial shade' ||
    normalized === 'full-sun-to-part-shade' ||
    normalized === 'full sun to part shade'
  ) {
    return 'full-sun-to-partial-shade';
  }
  if (normalized === 'partial-shade' || normalized === 'partial shade' || normalized === 'partial-sun' || normalized === 'partial sun') {
    return 'partial-shade';
  }
  return 'full-sun';
}

function normalizeWater(value: unknown): Plant['water'] {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'low-to-moderate' || normalized === 'low to moderate' || normalized === 'low/moderate') {
    return 'low-to-moderate';
  }
  if (normalized === 'low' || normalized === 'high') return normalized;
  return 'moderate';
}

function normalizeZones(value: unknown): number[] {
  const zones = Array.isArray(value)
    ? value.map(zone => typeof zone === 'number' ? zone : Number(zone))
    : typeof value === 'string'
      ? value.split(/[,\s]+/).map(zone => Number(zone))
      : [];

  return Array.from(
    new Set(zones.filter(zone => Number.isFinite(zone) && zone >= 1 && zone <= 13)),
  ).sort((a, b) => a - b);
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map(item => item.trim());
  }

  if (typeof value === 'string') {
    return value
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizePhenologyList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map(item => item.trim());
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
}

function isPhenologyClimateBand(value: unknown): value is PlantPhenologyClimateBand {
  return (
    value === 'cold' ||
    value === 'cool' ||
    value === 'mild' ||
    value === 'warm' ||
    value === 'hot' ||
    value === 'mildWinter'
  );
}

function normalizePhenologyLocationNotes(value: unknown): PlantPhenologyPhase['locationNotes'] {
  if (!Array.isArray(value)) return undefined;

  const notes = value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
    .map(item => {
      const note = typeof item.note === 'string' ? item.note.trim() : '';
      if (!note) return null;

      const zones = Array.isArray(item.zones)
        ? normalizeZones(
            item.zones
              .map(zone => typeof zone === 'number' ? zone : typeof zone === 'string' ? Number(zone) : NaN)
              .filter((zone): zone is number => Number.isFinite(zone)),
          )
        : [];
      const climateBand = isPhenologyClimateBand(item.climateBand) ? item.climateBand : undefined;

      return {
        ...(climateBand ? { climateBand } : {}),
        ...(zones.length > 0 ? { zones } : {}),
        note,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return notes.length > 0 ? notes : undefined;
}

function normalizePhenologyPhase(value: unknown, additionalCues: string[] = []): PlantPhenologyPhase | string[] | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const source = value as Record<string, unknown>;
    const summary = typeof source.summary === 'string' ? source.summary.trim() : '';
    const cues = uniqueList([
      ...normalizePhenologyList(source.cues),
      ...additionalCues,
    ]);
    const locationNotes = normalizePhenologyLocationNotes(source.locationNotes);

    if (!summary && cues.length === 0 && !locationNotes) return undefined;

    return {
      ...(summary ? { summary } : {}),
      cues,
      ...(locationNotes ? { locationNotes } : {}),
    };
  }

  const cues = uniqueList([
    ...normalizePhenologyList(value),
    ...additionalCues,
  ]);

  return cues.length > 0 ? cues : undefined;
}

function uniqueList(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function normalizePhenology(value: unknown): Plant['phenology'] | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const source = value as {
    indoorStart?: unknown;
    indoor?: unknown;
    directSow?: unknown;
    transplant?: unknown;
  };
  const indoorStart = normalizePhenologyPhase(source.indoorStart, normalizePhenologyList(source.indoor));
  const directSow = normalizePhenologyPhase(source.directSow);
  const transplant = normalizePhenologyPhase(source.transplant);
  const normalized: NonNullable<Plant['phenology']> = {
    ...(indoorStart ? { indoorStart } : {}),
    ...(directSow ? { directSow } : {}),
    ...(transplant ? { transplant } : {}),
  };

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function normalizeDaysToMaturity(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${Math.round(value)} days`;
  }
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? `${trimmed} days` : trimmed;
  }
  return '75 days';
}

function normalizePlanting(value: Record<string, unknown>): Plant['planting'] {
  const planting = asRecord(value.planting);
  return {
    ...(firstString(planting.indoorStart, value.indoorStart) ? { indoorStart: firstString(planting.indoorStart, value.indoorStart) } : {}),
    ...(firstString(planting.transplant, value.transplant) ? { transplant: firstString(planting.transplant, value.transplant) } : {}),
    ...(firstString(planting.directSow, value.directSow) ? { directSow: firstString(planting.directSow, value.directSow) } : {}),
    ...(firstString(planting.harvest, value.harvest) ? { harvest: firstString(planting.harvest, value.harvest) } : {}),
  };
}

function normalizeTempRequirements(
  value: unknown,
  fallbackType: Plant['type'],
): PlantTemperatureRequirements {
  const source = asRecord(value);
  const normalizedSeason =
    source.season === 'cool' || source.season === 'warm' || source.season === 'perennial' || source.season === 'fall-planted'
      ? source.season
      : undefined;
  const fallback = getEffectiveTempRequirements({ type: fallbackType, season: normalizedSeason });

  return {
    minSoilTempF: typeof source.minSoilTempF === 'number' ? source.minSoilTempF : fallback.minSoilTempF,
    minNightTempF: typeof source.minNightTempF === 'number' ? source.minNightTempF : fallback.minNightTempF,
    frostTolerant: typeof source.frostTolerant === 'boolean' ? source.frostTolerant : fallback.frostTolerant,
    hardFrostTolerant: typeof source.hardFrostTolerant === 'boolean'
      ? source.hardFrostTolerant
      : fallback.hardFrostTolerant,
    heatSensitiveAboveF: typeof source.heatSensitiveAboveF === 'number'
      ? source.heatSensitiveAboveF
      : fallback.heatSensitiveAboveF,
    season: normalizedSeason ?? fallback.season,
    plantingNote: typeof source.plantingNote === 'string' && source.plantingNote.trim()
      ? source.plantingNote.trim()
      : fallback.plantingNote,
  };
}

function normalizeHarvestWindow(value: unknown): Plant['harvestWindow'] | undefined {
  const source = asRecord(value);
  const months = Array.isArray(source.months)
    ? source.months
        .map(month => typeof month === 'number' ? month : Number(month))
        .filter(month => Number.isFinite(month) && month >= 1 && month <= 12)
    : [];

  if (!months.length) return undefined;

  const zoneShift = asRecord(source.zoneShift);
  const normalizedZoneShift = zoneShift && (
    typeof zoneShift.cold === 'number' ||
    typeof zoneShift.mid === 'number' ||
    typeof zoneShift.warm === 'number'
  )
    ? {
        ...(typeof zoneShift.cold === 'number' ? { cold: zoneShift.cold } : {}),
        ...(typeof zoneShift.mid === 'number' ? { mid: zoneShift.mid } : {}),
        ...(typeof zoneShift.warm === 'number' ? { warm: zoneShift.warm } : {}),
      }
    : undefined;

  return {
    months: Array.from(new Set(months)).sort((a, b) => a - b),
    ...(normalizedZoneShift ? { zoneShift: normalizedZoneShift } : {}),
  };
}

const SEED_STARTING_METHODS: Array<NonNullable<PlantSeedStarting['bestMethod']>> = [
  'start-indoors',
  'direct-sow',
  'either',
  'transplant-only',
];

function trimSeedStartingField(value: unknown): string | undefined {
  return typeof value === 'string' ? value.trim() || undefined : undefined;
}

function normalizeSeedStartingList(value: unknown): string[] {
  return Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map(item => item.trim())
    : [];
}

function normalizeHardeningOff(value: unknown): PlantSeedStarting['hardeningOff'] | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const protocol = value as {
    duration?: unknown;
    steps?: unknown;
    criticalWarnings?: unknown;
  };
  const normalized = {
    ...(trimSeedStartingField(protocol.duration) ? { duration: trimSeedStartingField(protocol.duration) } : {}),
    ...(normalizeSeedStartingList(protocol.steps).length > 0 ? { steps: normalizeSeedStartingList(protocol.steps) } : {}),
    ...(normalizeSeedStartingList(protocol.criticalWarnings).length > 0
      ? { criticalWarnings: normalizeSeedStartingList(protocol.criticalWarnings) }
      : {}),
  };

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function normalizeSeedStarting(value: unknown): PlantSeedStarting | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Partial<PlantSeedStarting> & { hardeningOff?: unknown };
  const rawHardeningOff = source.hardeningOff;
  const hardeningOff = normalizeHardeningOff(rawHardeningOff);
  const legacyHardeningOff = trimSeedStartingField(rawHardeningOff);

  const normalized: PlantSeedStarting = {
    ...(source.bestMethod && SEED_STARTING_METHODS.includes(source.bestMethod) ? { bestMethod: source.bestMethod } : {}),
    ...(trimSeedStartingField(source.substrate) ? { substrate: trimSeedStartingField(source.substrate) } : {}),
    ...(trimSeedStartingField(source.sowDepth) ? { sowDepth: trimSeedStartingField(source.sowDepth) } : {}),
    ...(trimSeedStartingField(source.preSoak) ? { preSoak: trimSeedStartingField(source.preSoak) } : {}),
    ...(trimSeedStartingField(source.germinationTemp) ? { germinationTemp: trimSeedStartingField(source.germinationTemp) } : {}),
    ...(trimSeedStartingField(source.germinationLight) ? { germinationLight: trimSeedStartingField(source.germinationLight) } : {}),
    ...(trimSeedStartingField(source.lightAfterSprouting) ? { lightAfterSprouting: trimSeedStartingField(source.lightAfterSprouting) } : {}),
    ...(trimSeedStartingField(source.lightDistance) ? { lightDistance: trimSeedStartingField(source.lightDistance) } : {}),
    ...(trimSeedStartingField(source.moisture) ? { moisture: trimSeedStartingField(source.moisture) } : {}),
    ...(trimSeedStartingField(source.humidityDome) ? { humidityDome: trimSeedStartingField(source.humidityDome) } : {}),
    ...(trimSeedStartingField(source.airflow) ? { airflow: trimSeedStartingField(source.airflow) } : {}),
    ...(trimSeedStartingField(source.pottingUp) ? { pottingUp: trimSeedStartingField(source.pottingUp) } : {}),
    ...(trimSeedStartingField(source.transplantReadiness) ? { transplantReadiness: trimSeedStartingField(source.transplantReadiness) } : {}),
    ...(hardeningOff ? { hardeningOff } : {}),
    ...(Array.isArray(source.specialNotes)
      ? {
          specialNotes: normalizeSeedStartingList(source.specialNotes),
        }
      : {}),
    ...(trimSeedStartingField(source.indoorNotes) ? { indoorNotes: trimSeedStartingField(source.indoorNotes) } : {}),
    ...(trimSeedStartingField(source.directSowNotes) ? { directSowNotes: trimSeedStartingField(source.directSowNotes) } : {}),
    ...(trimSeedStartingField(source.germinationNotes) ? { germinationNotes: trimSeedStartingField(source.germinationNotes) } : {}),
    ...(trimSeedStartingField(source.pottingUpNotes) ? { pottingUpNotes: trimSeedStartingField(source.pottingUpNotes) } : {}),
    ...(trimSeedStartingField(source.hardeningOffNotes) || legacyHardeningOff
      ? { hardeningOffNotes: trimSeedStartingField(source.hardeningOffNotes) ?? legacyHardeningOff }
      : {}),
  };

  if (normalized.specialNotes?.length === 0) delete normalized.specialNotes;

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function normalizeTraditionalMedicinalUse(value: unknown): Plant['traditionalMedicinalUse'] | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const source = asRecord(value);
  if (typeof source.overview !== 'string' || !source.overview.trim()) return undefined;

  const partsUsed = normalizeStringList(source.partsUsed);
  const historicalContexts = Array.isArray(source.historicalContexts)
    ? source.historicalContexts
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
        .map(item => ({
          ...(typeof item.tradition === 'string' && item.tradition.trim() ? { tradition: item.tradition.trim() } : {}),
          ...(typeof item.plantPart === 'string' && item.plantPart.trim() ? { plantPart: item.plantPart.trim() } : {}),
          description: typeof item.description === 'string' ? item.description.trim() : '',
        }))
        .filter(item => item.description)
    : [];

  return {
    overview: source.overview.trim(),
    ...(partsUsed.length > 0 ? { partsUsed } : {}),
    ...(historicalContexts.length > 0 ? { historicalContexts } : {}),
    ...(typeof source.safetyNote === 'string' && source.safetyNote.trim() ? { safetyNote: source.safetyNote.trim() } : {}),
  };
}

function normalizeTaxonomy(value: unknown): PlantTaxonomy | undefined {
  const source = asRecord(value);
  if (
    typeof source.kingdom !== 'string' ||
    typeof source.family !== 'string' ||
    typeof source.genus !== 'string' ||
    typeof source.species !== 'string' ||
    typeof source.commonFamily !== 'string'
  ) {
    return undefined;
  }

  return {
    kingdom: source.kingdom.trim(),
    family: source.family.trim(),
    genus: source.genus.trim(),
    species: source.species.trim(),
    commonFamily: source.commonFamily.trim(),
  };
}

function normalizeMorphology(value: unknown): PlantMorphology | undefined {
  const source = asRecord(value);
  if (
    typeof source.rootSystem !== 'string' ||
    typeof source.stem !== 'string' ||
    typeof source.leaves !== 'string' ||
    typeof source.flowers !== 'string' ||
    typeof source.fruit !== 'string'
  ) {
    return undefined;
  }

  return {
    rootSystem: source.rootSystem.trim(),
    stem: source.stem.trim(),
    leaves: source.leaves.trim(),
    flowers: source.flowers.trim(),
    fruit: source.fruit.trim(),
  };
}

function normalizeVarieties(value: unknown): PlantVariety[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const varieties = value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
    .map(item => ({
      name: typeof item.name === 'string' ? item.name.trim() : '',
      description: typeof item.description === 'string' ? item.description.trim() : '',
      ...(typeof item.bestFor === 'string' && item.bestFor.trim() ? { bestFor: item.bestFor.trim() } : {}),
    }))
    .filter(item => item.name && item.description);

  return varieties.length > 0 ? varieties : undefined;
}

const TRADITIONAL_MEDICINAL_USE_COMPLETENESS: NonNullable<PlantAuthoringCompleteness['traditionalMedicinalUse']>[] = [
  'complete',
  'not_applicable',
];
const VARIETIES_COMPLETENESS: NonNullable<PlantAuthoringCompleteness['varieties']>[] = [
  'complete',
  'not_useful',
];

function normalizeAuthoringCompleteness(value: unknown): PlantAuthoringCompleteness | undefined {
  const source = asRecord(value);
  const normalized: PlantAuthoringCompleteness = {};

  if (TRADITIONAL_MEDICINAL_USE_COMPLETENESS.includes(source.traditionalMedicinalUse as NonNullable<PlantAuthoringCompleteness['traditionalMedicinalUse']>)) {
    normalized.traditionalMedicinalUse = source.traditionalMedicinalUse as NonNullable<PlantAuthoringCompleteness['traditionalMedicinalUse']>;
  }

  if (VARIETIES_COMPLETENESS.includes(source.varieties as NonNullable<PlantAuthoringCompleteness['varieties']>)) {
    normalized.varieties = source.varieties as NonNullable<PlantAuthoringCompleteness['varieties']>;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

const NATIVE_RANGE_REVIEW_STATUSES: PlantNativeRange['reviewStatus'][] = ['reviewed', 'unreviewed'];
const NATIVE_RANGE_IMPORT_CONFIDENCES: NonNullable<PlantNativeRange['importConfidence']>[] = ['high', 'medium', 'low'];
const NATIVE_RANGE_IMPORT_STATUSES: NonNullable<PlantNativeRange['importStatus']>[] = ['not_started', 'imported', 'failed'];
const PLANT_RANGE_STATUSES: PlantRangeStatus[] = ['native', 'introduced', 'naturalized', 'cultivated', 'uncertain'];

function normalizeNativeRangeRegion(value: unknown): PlantNativeRange['regions'][number] | null {
  const source = asRecord(value);
  const code = firstString(source.code)?.toUpperCase();
  const status = PLANT_RANGE_STATUSES.find(candidate => candidate === source.status);

  if (!code || !status) return null;

  return {
    code,
    status,
    ...(firstString(source.note) ? { note: firstString(source.note) } : {}),
  };
}

function normalizeNativeRange(value: unknown): PlantNativeRange | undefined {
  const source = asRecord(value);
  if (Object.keys(source).length === 0) return undefined;

  const reviewStatus = NATIVE_RANGE_REVIEW_STATUSES.find(candidate => candidate === source.reviewStatus) ?? 'unreviewed';
  const importConfidence = NATIVE_RANGE_IMPORT_CONFIDENCES.find(candidate => candidate === source.importConfidence);
  const importStatus = NATIVE_RANGE_IMPORT_STATUSES.find(candidate => candidate === source.importStatus);
  const regions = Array.isArray(source.regions)
    ? source.regions
        .map(normalizeNativeRangeRegion)
        .filter((region): region is PlantNativeRange['regions'][number] => Boolean(region))
    : [];
  const sourceNotes = normalizeStringList(source.sourceNotes);
  const broaderNativeRange = normalizeStringList(source.broaderNativeRange);
  const origin = firstString(source.origin);
  const nativeHabitat = firstString(source.nativeHabitat);
  const currentDistribution = firstString(source.currentDistribution);
  const importSource = firstString(source.importSource);
  const importAttemptedAt = firstString(source.importAttemptedAt);

  const hasRangeDetail =
    regions.length > 0 ||
    sourceNotes.length > 0 ||
    broaderNativeRange.length > 0 ||
    Boolean(origin || nativeHabitat || currentDistribution || importSource || importConfidence || importStatus || importAttemptedAt);

  if (!hasRangeDetail) return undefined;

  return {
    reviewStatus,
    ...(firstString(source.reviewedBy) ? { reviewedBy: firstString(source.reviewedBy) } : {}),
    ...(firstString(source.reviewedAt) ? { reviewedAt: firstString(source.reviewedAt) } : {}),
    ...(importSource ? { importSource } : {}),
    ...(importConfidence ? { importConfidence } : {}),
    ...(importStatus ? { importStatus } : {}),
    ...(importAttemptedAt ? { importAttemptedAt } : {}),
    ...(sourceNotes.length > 0 ? { sourceNotes } : {}),
    regions,
    ...(broaderNativeRange.length > 0 ? { broaderNativeRange } : {}),
    ...(origin ? { origin } : {}),
    ...(nativeHabitat ? { nativeHabitat } : {}),
    ...(currentDistribution ? { currentDistribution } : {}),
  };
}

const LIFECYCLE_KINDS: PlantLifecycle['kind'][] = ['annual', 'biennial', 'perennial', 'tender-perennial', 'fall-planted'];
const PLANTING_MATERIALS: NonNullable<PlantLifecycle['plantingMaterial']>[] = [
  'seed',
  'transplant',
  'crown',
  'bare-root',
  'cane',
  'tree',
  'shrub',
  'vine',
  'tuber',
  'slip',
  'division',
  'clove',
  'bulb',
  'root-cutting',
];
const HARVEST_TASK_MODES: NonNullable<PlantLifecycle['harvestTaskMode']>[] = [
  'days-to-maturity',
  'delayed-first-harvest',
  'seasonal-after-establishment',
  'none',
];

function normalizeNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeNonNegativeNumber(value: unknown): number | undefined {
  const normalized = normalizeNumber(value);
  return normalized !== undefined && normalized >= 0 ? normalized : undefined;
}

function normalizeLifecycle(value: unknown): PlantLifecycle | undefined {
  const source = asRecord(value);
  const kind = LIFECYCLE_KINDS.find(candidate => candidate === source.kind);
  if (!kind) return undefined;

  const plantingMaterial = PLANTING_MATERIALS.find(candidate => candidate === source.plantingMaterial);
  const harvestTaskMode = HARVEST_TASK_MODES.find(candidate => candidate === source.harvestTaskMode);
  const timelineMilestones = Array.isArray(source.timelineMilestones)
    ? source.timelineMilestones
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
        .map(item => {
          const label = typeof item.label === 'string' ? item.label.trim() : '';
          const pct = normalizeNumber(item.pct);
          return label && pct !== undefined ? { label, pct } : null;
        })
        .filter((item): item is { label: string; pct: number } => Boolean(item))
    : [];

  return {
    kind,
    ...(plantingMaterial ? { plantingMaterial } : {}),
    ...(normalizeNonNegativeNumber(source.establishmentDays) !== undefined
      ? { establishmentDays: normalizeNonNegativeNumber(source.establishmentDays) }
      : {}),
    ...(source.firstHarvestOffsetDays === null
      ? { firstHarvestOffsetDays: null }
      : normalizeNonNegativeNumber(source.firstHarvestOffsetDays) !== undefined
        ? { firstHarvestOffsetDays: normalizeNonNegativeNumber(source.firstHarvestOffsetDays) }
        : {}),
    ...(harvestTaskMode ? { harvestTaskMode } : {}),
    ...(firstString(source.establishmentLabel) ? { establishmentLabel: firstString(source.establishmentLabel) } : {}),
    ...(firstString(source.firstHarvestNote) ? { firstHarvestNote: firstString(source.firstHarvestNote) } : {}),
    ...(timelineMilestones.length > 0 ? { timelineMilestones } : {}),
  };
}

function normalizeStatus(value: unknown, fallback: Plant['status'] = 'approved'): Plant['status'] {
  return value === 'draft' || value === 'approved' ? value : fallback;
}

function normalizeSource(
  value: unknown,
  defaultSource: PlantSource,
  sourceOverride?: Partial<PlantSource>,
): PlantSource {
  const source = asRecord(value);
  const kind = source.kind === 'curated' || source.kind === 'community' || source.kind === 'generated'
    ? source.kind
    : defaultSource.kind;
  const trust = source.trust === 'curated' || source.trust === 'community' || source.trust === 'generated'
    ? source.trust
    : defaultSource.trust;

  const normalized: PlantSource = {
    kind,
    trust,
    reviewed: typeof source.reviewed === 'boolean' ? source.reviewed : defaultSource.reviewed,
    generatedAt: firstString(source.generatedAt, defaultSource.generatedAt),
    promotedAt: firstString(source.promotedAt, defaultSource.promotedAt),
    promotedBy: firstString(source.promotedBy, defaultSource.promotedBy) ?? null,
    promotedToBuiltin: typeof source.promotedToBuiltin === 'boolean'
      ? source.promotedToBuiltin
      : defaultSource.promotedToBuiltin,
    builtinId: firstString(source.builtinId, defaultSource.builtinId),
    builtinPromotedAt: firstString(source.builtinPromotedAt, defaultSource.builtinPromotedAt),
    builtinPromotedBy: firstString(source.builtinPromotedBy, defaultSource.builtinPromotedBy) ?? null,
  };

  if (sourceOverride) {
    if (sourceOverride.kind) normalized.kind = sourceOverride.kind;
    if (sourceOverride.trust) normalized.trust = sourceOverride.trust;
    if (sourceOverride.reviewed !== undefined) normalized.reviewed = sourceOverride.reviewed;
    if (sourceOverride.generatedAt !== undefined) normalized.generatedAt = sourceOverride.generatedAt;
    if (sourceOverride.promotedAt !== undefined) normalized.promotedAt = sourceOverride.promotedAt;
    if (sourceOverride.promotedBy !== undefined) normalized.promotedBy = sourceOverride.promotedBy;
    if (sourceOverride.promotedToBuiltin !== undefined) normalized.promotedToBuiltin = sourceOverride.promotedToBuiltin;
    if (sourceOverride.builtinId !== undefined) normalized.builtinId = sourceOverride.builtinId;
    if (sourceOverride.builtinPromotedAt !== undefined) normalized.builtinPromotedAt = sourceOverride.builtinPromotedAt;
    if (sourceOverride.builtinPromotedBy !== undefined) normalized.builtinPromotedBy = sourceOverride.builtinPromotedBy;
  }

  if (normalized.generatedAt === undefined) delete normalized.generatedAt;
  if (normalized.promotedAt === undefined) delete normalized.promotedAt;
  if (normalized.promotedBy === null || normalized.promotedBy === undefined) delete normalized.promotedBy;
  if (normalized.promotedToBuiltin === undefined) delete normalized.promotedToBuiltin;
  if (normalized.builtinId === undefined) delete normalized.builtinId;
  if (normalized.builtinPromotedAt === undefined) delete normalized.builtinPromotedAt;
  if (normalized.builtinPromotedBy === null || normalized.builtinPromotedBy === undefined) delete normalized.builtinPromotedBy;

  return normalized;
}

export function normalizePlantInput(value: unknown, options: NormalizePlantInputOptions = {}): CanonicalPlant {
  const source = asRecord(value);
  const sourceRecord = asRecord(source.source);
  const generatedAt = firstString(source.generatedAt, sourceRecord.generatedAt, options.defaultSource?.generatedAt);
  const type = normalizePlantCategoryInput(source.type ?? source.category);
  const traits = normalizePlantTraits(source.traits, source.plantTraits, source.tags);
  const planting = normalizePlanting(source);
  const soilType = firstString(source.soilType, source.soil) ?? 'Well-draining garden soil';
  const antagonists = normalizeStringList(
    Array.isArray(source.antagonists) || typeof source.antagonists === 'string'
      ? source.antagonists
      : source.avoid,
  );
  const pests = normalizeStringList(
    Array.isArray(source.pests) || typeof source.pests === 'string'
      ? source.pests
      : source.commonPests,
  );
  const defaultSource = options.defaultSource ?? {
    kind: 'community',
    trust: 'community',
    reviewed: true,
    ...(generatedAt ? { generatedAt } : {}),
  };
  const normalized: CanonicalPlant = {
    id: firstString(source.id) ?? options.fallbackId ?? `generated-${Date.now()}`,
    name: firstString(source.name) ?? 'Unnamed Plant',
    scientificName: firstString(source.scientificName) ?? 'Unknown species',
    status: normalizeStatus(source.status, options.defaultStatus ?? 'approved'),
    type,
    ...(traits ? { traits } : {}),
    description: firstString(source.description) ?? options.fallbackDescription ?? 'Community plant profile.',
    zones: normalizeZones(source.zones),
    sunlight: normalizeSunlight(source.sunlight),
    water: normalizeWater(source.water),
    soilType,
    spacing: firstString(source.spacing) ?? '12 inches',
    daysToMaturity: normalizeDaysToMaturity(source.daysToMaturity),
    companions: normalizeStringList(source.companions),
    antagonists,
    planting,
    pests,
    organicTips: normalizeStringList(source.organicTips),
    tempRequirements: normalizeTempRequirements(source.tempRequirements, type),
    source: normalizeSource(source.source, defaultSource, options.sourceOverride),
  };

  const harvestWindow = normalizeHarvestWindow(source.harvestWindow);
  if (harvestWindow) normalized.harvestWindow = harvestWindow;
  const phenology = normalizePhenology(source.phenology);
  if (phenology) normalized.phenology = phenology;
  const seedStarting = normalizeSeedStarting(source.seedStarting);
  if (seedStarting) normalized.seedStarting = seedStarting;
  const lifecycle = normalizeLifecycle(source.lifecycle);
  if (lifecycle) normalized.lifecycle = lifecycle;
  const taxonomy = normalizeTaxonomy(source.taxonomy);
  if (taxonomy) normalized.taxonomy = taxonomy;
  const naturalHistory = firstString(source.naturalHistory);
  if (naturalHistory) normalized.naturalHistory = naturalHistory;
  const traditionalMedicinalUse = normalizeTraditionalMedicinalUse(source.traditionalMedicinalUse);
  if (traditionalMedicinalUse) normalized.traditionalMedicinalUse = traditionalMedicinalUse;
  const morphology = normalizeMorphology(source.morphology);
  if (morphology) normalized.morphology = morphology;
  const varieties = normalizeVarieties(source.varieties);
  if (varieties) normalized.varieties = varieties;
  const nativeRange = normalizeNativeRange(source.nativeRange);
  if (nativeRange) normalized.nativeRange = nativeRange;
  const authoringCompleteness = normalizeAuthoringCompleteness(source.authoringCompleteness);
  if (authoringCompleteness) normalized.authoringCompleteness = authoringCompleteness;
  const image = firstString(source.image);
  if (image) normalized.image = image;

  if (options.includeCompatibilityAliases) {
    normalized.isGenerated = true;
    normalized.soil = soilType;
    normalized.avoid = antagonists;
    normalized.commonPests = pests;
    normalized.indoorStart = planting.indoorStart ?? null;
    normalized.transplant = planting.transplant ?? null;
    normalized.directSow = planting.directSow ?? null;
    normalized.harvest = planting.harvest ?? '';
  }

  return normalized;
}

export function normalizeBuiltInPlant(plant: Plant): CanonicalPlant {
  return normalizePlantInput(plant, {
    defaultSource: {
      kind: 'curated',
      trust: 'curated',
      reviewed: true,
    },
    defaultStatus: 'approved',
  });
}

export function normalizeCommunityPlant(plant: Plant): CanonicalPlant {
  return normalizePlantInput(plant, {
    defaultSource: {
      kind: 'community',
      trust: 'community',
      reviewed: true,
    },
    defaultStatus: plant.status ?? 'approved',
  });
}

export function normalizeGeneratedPlantToPlant(plant: GeneratedPlant): CanonicalPlant {
  const plantWithoutNativeRange = { ...plant };
  delete plantWithoutNativeRange.nativeRange;

  return normalizePlantInput(plantWithoutNativeRange, {
    defaultSource: {
      kind: 'community',
      trust: 'generated',
      reviewed: false,
      generatedAt: plant.generatedAt,
    },
    defaultStatus: 'draft',
    fallbackDescription: 'Community plant profile.',
    includeCompatibilityAliases: true,
  });
}
